<template>
    <div class="h-8 grid gap-3 items-center grid-cols-[1rem_1fr]">
        <LinkIcon v-if="props.variant === 'Link-External' || props.variant === 'Link-Internal'" class="text-gray-500" />

        <div v-if="props.variant === 'Link-External'" class="grid gap-3 items-center grid-cols-[1fr_1rem]">
            <div class="flex flex-row items-center gap-1.5">
                <a
                    class="leading-tight text-sm font-semibold border-b border-b-sky-800 hover:border-b-sky-900 active:border-b-sky-950 text-sky-800 hover:text-sky-900 active:text-sky-950 focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-sky-700"
                    :href="props.linkTarget"
                    target="_blank"
                >
                    {{ props.label }}
                </a>

                <Tooltip v-if="isOneNoteLink">
                    <template #trigger="tooltipProps">
                        <OneNoteIcon
                            class="focus-visible:outline-offset-3 focus-visible:outline-2 focus-visible:rounded-xs cursor-help"
                            tabindex="0"
                            v-bind="tooltipProps"
                        />
                    </template>
                    <template #text>
                        {{ $t('one-note-link') }}
                    </template>
                </Tooltip>

                <HelpButton v-if="props.helpButtonText" class="text-gray-500" :text="props.helpButtonText" />
            </div>
            <Tooltip>
                <template #trigger="tooltipProps">
                    <ExternalLinkIcon
                        v-if="props.variant === 'Link-External'"
                        class="text-gray-500"
                        aria-hidden="true"
                        tabindex="0"
                        v-bind="tooltipProps"
                    />
                </template>
                <template #text>{{ $t('external-link') }}</template>
            </Tooltip>
        </div>

        <div v-if="props.variant === 'Link-Internal'" class="flex flex-row items-center gap-1 min-w-0">
            <Tooltip :is-teleported="true" :is-disabled="!isTruncated" class="min-w-0">
                <template #trigger>
                    <RouterLink
                        ref="linkComponent"
                        class="leading-tight text-sm line-clamp-1 font-semibold border-b border-b-sky-800 hover:border-b-sky-900 active:border-b-sky-950 text-sky-800 hover:text-sky-900 active:text-sky-950 focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-sky-700"
                        :to="props.linkTarget"
                        target="_blank"
                    >
                        {{ props.label }}
                    </RouterLink>
                </template>
                <template #text>
                    <div class="max-w-md text-left">{{ props.label }}</div>
                </template>
            </Tooltip>

            <HelpButton v-if="props.helpButtonText" class="text-gray-500" :text="props.helpButtonText" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { type ComponentPublicInstance, computed, ref, useTemplateRef, watch } from 'vue';
import { RouterLink } from 'vue-router';
import HelpButton from '../HelpButton/HelpButton.vue';
import ExternalLinkIcon from '../Icon/ExternalLinkIcon.vue';
import LinkIcon from '../Icon/LinkIcon.vue';
import OneNoteIcon from '../Icon/OneNoteIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';

type LinkAttachmentProps = {
    variant: 'Link-External' | 'Link-Internal';
    label: string;
    linkTarget: string;
    helpButtonText?: string;
};

const props = defineProps<LinkAttachmentProps>();

const isOneNoteLink = computed(() => props.linkTarget.startsWith('onenote:'));

const linkComponent = useTemplateRef<ComponentPublicInstance>('linkComponent');
const isTruncated = ref(false);

function updateIsTruncated() {
    const linkElement: HTMLElement | undefined = linkComponent.value?.$el;
    // line-clamp cuts off further lines vertically; a single unbreakable word overflows horizontally
    isTruncated.value = linkElement
        ? linkElement.scrollHeight > linkElement.clientHeight || linkElement.scrollWidth > linkElement.clientWidth
        : false;
}

useResizeObserver(linkComponent, updateIsTruncated);
watch(() => props.label, updateIsTruncated, { flush: 'post' });
</script>
